import React, { useRef, useState, useEffect } from 'react'
import { Spin, message } from 'antd'
import { CarEntry } from './CarWithDriverAddCarForm'
import { TopBar } from './CarWithDriverShared'
import { useCwdLang } from './useCwdLang'
import { useAuth } from '../../contexts/Auth'
import { uploadPhotoFile } from '../../services/upload/upload.service'

const successCheckmarkStyle: React.CSSProperties = {
  width: '80px',
  height: '80px',
  borderRadius: '50%',
  backgroundColor: '#00CCCC',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  margin: '0 auto 20px',
  animation: 'scaleIn 0.6s cubic-bezier(0.175, 0.885, 0.32, 1.275) forwards',
}

export interface Step5Data {
  idCardUrl: string
  bankBookUrl: string
  isBankHolderSameAsOwner: boolean | null
  consentLetterUrl: string
  cars: Record<string, CarDocumentEntry>
}

export interface CarDocumentEntry {
  platePrefix: string
  plateNumber: string
  registrationProvince: string
  vin: string
  taxExpirationDate: string
  isCompanyRegistered: boolean | null
  isDirector: boolean | null
  companyCertificateUrl: string
  registrationBookUrl: string
  taxLabelUrl: string
}

export const defaultStep5Data: Step5Data = {
  idCardUrl: '',
  bankBookUrl: '',
  isBankHolderSameAsOwner: null,
  consentLetterUrl: '',
  cars: {},
}

interface Props {
  data: Step5Data
  cars: CarEntry[]
  onDataChange: (d: Step5Data) => void
  currentStep: number
  totalSteps: number
  draftId: number | null
  onExit: () => void
  onBack: () => void
  onNext: () => Promise<void>
  onPublish: () => Promise<void>
}

const CarWithDriverStep5: React.FC<Props> = ({
  data,
  cars,
  onDataChange,
  currentStep,
  totalSteps,
  draftId,
  onExit,
  onBack,
  onNext,
  onPublish,
}) => {
  const { userLoggedIn } = useAuth()
  const { t } = useCwdLang()
  const [selectedCarId, setSelectedCarId] = useState<string | null>(null)
  const [isSubmitting, setIsSubmitting] = useState(false)
  const [showSuccess, setShowSuccess] = useState(false)
  const [showSuccessLoading, setShowSuccessLoading] = useState(false)
  const [successVisible, setSuccessVisible] = useState(false)
  const [uploading, setUploading] = useState<Record<string, boolean>>({})

  const fileInputRef = useRef<HTMLInputElement>(null)
  const [activeUploadSlot, setActiveUploadSlot] = useState<string | null>(null)

  useEffect(() => {
    const style = document.createElement('style')
    style.innerHTML = `
      @keyframes scaleIn {
        0% { transform: scale(0); opacity: 0; }
        100% { transform: scale(1); opacity: 1; }
      }
      @keyframes fadeSlideUp {
        0% { opacity: 0; transform: translateY(24px); }
        100% { opacity: 1; transform: translateY(0); }
      }
      @keyframes pulseRing {
        0% { transform: scale(0.95); box-shadow: 0 0 0 0 rgba(0, 204, 204, 0.5); }
        70% { transform: scale(1); box-shadow: 0 0 0 20px rgba(0, 204, 204, 0); }
        100% { transform: scale(0.95); box-shadow: 0 0 0 0 rgba(0, 204, 204, 0); }
      }
      @keyframes spinnerRotate {
        0% { transform: rotate(0deg); }
        100% { transform: rotate(360deg); }
      }
      @keyframes successBg {
        0% { opacity: 0; }
        100% { opacity: 1; }
      }
    `
    document.head.appendChild(style)
    return () => {
      document.head.removeChild(style)
    }
  }, [])

  useEffect(() => {
    if (!showSuccessLoading) return
    const timer = setTimeout(() => {
      setShowSuccessLoading(false)
      setShowSuccess(true)
      window.history.replaceState(null, '', window.location.pathname)
      setTimeout(() => setSuccessVisible(true), 50)
    }, 2000)
    return () => clearTimeout(timer)
  }, [showSuccessLoading])

  const expandedCars: { id: string; label: string; carType: string }[] = []
  if (Array.isArray(cars)) {
    cars.forEach((car) => {
      const qty = car.quantity || 1
      for (let i = 1; i <= qty; i++) {
        expandedCars.push({
          id: `${car.id}-${i}`,
          label: t.step5.carLabel(car.carType, i),
          carType: car.carType,
        })
      }
    })
  }

  const handleUploadClick = (slot: string) => {
    setActiveUploadSlot(slot)
    setTimeout(() => {
      if (fileInputRef.current) {
        fileInputRef.current.value = ''
        fileInputRef.current.click()
      }
    }, 10)
  }

  const handleFileChange = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0]
    if (!file || !activeUploadSlot) return

    setUploading((prev) => ({ ...prev, [activeUploadSlot]: true }))
    try {
      const url = await uploadPhotoFile(file, userLoggedIn?.userToken)
      if (activeUploadSlot.startsWith('car-')) {
        const parts = activeUploadSlot.split(':')
        const carId = parts[0].replace('car-', '')
        const field = parts[1] as keyof CarDocumentEntry
        const currentCarData = data.cars[carId] || {}
        onDataChange({
          ...data,
          cars: {
            ...data.cars,
            [carId]: { ...currentCarData, [field]: url },
          },
        })
      } else {
        onDataChange({ ...data, [activeUploadSlot as keyof Step5Data]: url })
      }
      message.success(t.step5.uploadSuccess)
    } catch (err: any) {
      message.error(err?.message || t.step5.uploadFailed)
    } finally {
      setUploading((prev) => ({ ...prev, [activeUploadSlot]: false }))
      setActiveUploadSlot(null)
      if (fileInputRef.current) fileInputRef.current.value = ''
    }
  }

  const handleCarFieldChange = (carId: string, field: keyof CarDocumentEntry, value: any) => {
    const currentCarData = data.cars[carId] || {}
    onDataChange({
      ...data,
      cars: {
        ...data.cars,
        [carId]: { ...currentCarData, [field]: value },
      },
    })
  }

  const handleSubmit = async () => {
    setIsSubmitting(true)
    await onPublish()
    setTimeout(() => {
      setIsSubmitting(false)
      setShowSuccessLoading(true)
    }, 800)
  }

  const renderUploadBox = (slot: string, currentUrl?: string) => {
    const isUp = uploading[slot]
    return (
      <div
        onClick={() => !isUp && handleUploadClick(slot)}
        style={{
          width: '100%',
          height: '140px',
          border: '1.5px dashed #D6DAE0',
          borderRadius: '12px',
          backgroundColor: '#F9FAFB',
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: 'center',
          cursor: isUp ? 'not-allowed' : 'pointer',
          color: '#0048B3',
          fontSize: '14px',
          fontWeight: '500',
          position: 'relative',
          overflow: 'hidden',
          transition: 'all 0.2s ease',
        }}
      >
        {isUp ? (
          <Spin />
        ) : currentUrl ? (
          <img
            src={currentUrl}
            alt="upload"
            style={{ width: '100%', height: '100%', objectFit: 'cover' }}
          />
        ) : (
          <>
            <img
              src="https://ts-upload.sgp1.digitaloceanspaces.com/cms/1zjzpxvw6prTsEgTxSLJLqwXKGp3W3eNtYibTcRtPAo3gM6zcTTTLZConyV8eQsTEMfJbyJu8N9mQpCFpKxua2W4.png"
              alt="icon"
              style={{ width: '32px', height: '32px', marginBottom: '8px', opacity: 0.6 }}
            />
            {t.common.uploadOrTakePhoto}
          </>
        )}
      </div>
    )
  }

  const renderSuccessLoadingView = () => (
    <div
      style={{
        fontFamily: 'Poppins, sans-serif',
        backgroundColor: '#fff',
        minHeight: '100vh',
        maxWidth: '500px',
        margin: '0 auto',
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        gap: '32px',
        animation: 'successBg 0.3s ease forwards',
      }}
    >
      <div style={{ position: 'relative', width: '96px', height: '96px' }}>
        <div
          style={{
            position: 'absolute',
            inset: 0,
            borderRadius: '50%',
            backgroundColor: 'rgba(0, 204, 204, 0.15)',
            animation: 'pulseRing 1.5s ease-out infinite',
          }}
        />
        <div
          style={{
            position: 'absolute',
            inset: '8px',
            borderRadius: '50%',
            backgroundColor: '#00CCCC',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
          }}
        >
          <div
            style={{
              width: '32px',
              height: '32px',
              border: '3px solid rgba(255,255,255,0.3)',
              borderTopColor: '#fff',
              borderRadius: '50%',
              animation: 'spinnerRotate 0.8s linear infinite',
            }}
          />
        </div>
      </div>
      <div style={{ textAlign: 'center' }}>
        <p
          style={{
            fontSize: '16px',
            fontWeight: '600',
            color: '#212427',
            margin: '0 0 6px',
          }}
        >
          {t.step5.processing}
        </p>
        <p style={{ fontSize: '13px', color: '#667085', margin: 0 }}>{t.step5.pleaseWait}</p>
      </div>
    </div>
  )

  const renderSuccessView = () => {
    const lineIcon =
      'https://ts-upload.sgp1.digitaloceanspaces.com/cms/17UmBKpMFpFyWcD2bkWqogewV3BahiXQ2ekiwzJvUDgA1gee6K3mQDaGMQGULEVoHJYpSTrPb57KaszybWfA6Loo.png'
    const lineUrl = 'https://line.me/R/ti/p/@haup.partner'
    return (
      <div
        style={{
          ...pageStyle,
          opacity: successVisible ? 1 : 0,
          transition: 'opacity 0.4s ease',
        }}
      >
        <div style={{ flex: 1, overflowY: 'auto', padding: '24px 16px' }}>
          <div
            style={{
              textAlign: 'center',
              marginBottom: '24px',
              animation: successVisible ? 'fadeSlideUp 0.5s ease forwards' : 'none',
            }}
          >
            <div style={successCheckmarkStyle}>
              <span style={{ color: '#fff', fontSize: '36px', fontWeight: '700' }}>✓</span>
            </div>
            <h2
              style={{
                fontSize: '22px',
                fontWeight: '700',
                color: '#212427',
                margin: '0 0 8px',
                lineHeight: '1.3',
              }}
            >
              {t.common.successTitle}
            </h2>
          </div>

          <div
            style={{
              border: '1px solid #D6DAE0',
              borderRadius: '12px',
              padding: '16px',
              marginBottom: '16px',
              backgroundColor: '#F9FAFB',
              opacity: successVisible ? 1 : 0,
              animation: successVisible ? 'fadeSlideUp 0.5s ease 0.2s both' : 'none',
            }}
          >
            <p style={{ fontSize: '15px', fontWeight: '700', color: '#212427', margin: '0 0 8px' }}>
              {t.common.connectLineTitle}
            </p>
            <p
              style={{ fontSize: '13px', color: '#667085', margin: '0 0 12px', lineHeight: '1.5' }}
            >
              {t.common.connectLineDesc}
            </p>
            <div
              onClick={() => window.open(lineUrl, '_blank', 'noopener,noreferrer')}
              style={{
                display: 'flex',
                alignItems: 'center',
                gap: '8px',
                border: '1px solid #D6DAE0',
                borderRadius: '24px',
                padding: '10px 14px',
                cursor: 'pointer',
                width: 'fit-content',
                backgroundColor: '#fff',
              }}
            >
              <img src={lineIcon} alt="LINE" style={{ width: '24px', height: '24px' }} />
              <span style={{ fontSize: '14px', fontWeight: '600', color: '#212427' }}>
                @haup.partner
              </span>
            </div>
          </div>

          <div
            style={{
              border: '1px solid #D6DAE0',
              borderRadius: '12px',
              padding: '16px',
              marginBottom: '16px',
              backgroundColor: '#F9FAFB',
              opacity: successVisible ? 1 : 0,
              animation: successVisible ? 'fadeSlideUp 0.5s ease 0.4s both' : 'none',
            }}
          >
            <p
              style={{ fontSize: '15px', fontWeight: '700', color: '#212427', margin: '0 0 16px' }}
            >
              {t.common.remainingSteps}
            </p>
            <div style={{ display: 'flex', gap: '12px', marginBottom: '16px' }}>
              <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
                <div
                  style={{
                    width: '24px',
                    height: '24px',
                    borderRadius: '50%',
                    backgroundColor: '#00CCCC',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                  }}
                >
                  <span style={{ color: '#fff', fontSize: '12px', fontWeight: '700' }}>✓</span>
                </div>
                <div
                  style={{
                    width: '2px',
                    height: '24px',
                    backgroundColor: '#D6DAE0',
                    margin: '4px 0',
                  }}
                />
              </div>
              <div>
                <p style={{ fontSize: '14px', fontWeight: '600', margin: '2px 0 0' }}>
                  {t.common.applicationSubmitted}
                </p>
              </div>
            </div>
            <div style={{ display: 'flex', gap: '12px', marginBottom: '16px' }}>
              <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
                <div
                  style={{
                    width: '24px',
                    height: '24px',
                    borderRadius: '50%',
                    backgroundColor: '#00CCCC',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                  }}
                >
                  <span style={{ color: '#fff', fontSize: '12px', fontWeight: '700' }}>✓</span>
                </div>
                <div
                  style={{
                    width: '2px',
                    height: '24px',
                    backgroundColor: '#D6DAE0',
                    margin: '4px 0',
                  }}
                />
              </div>
              <div>
                <p style={{ fontSize: '14px', fontWeight: '600', margin: '2px 0 0' }}>
                  {t.common.carListed}
                </p>
              </div>
            </div>
            <div style={{ display: 'flex', gap: '12px', marginBottom: '16px' }}>
              <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
                <div
                  style={{
                    width: '24px',
                    height: '24px',
                    borderRadius: '50%',
                    backgroundColor: '#00CCCC',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                  }}
                >
                  <span style={{ color: '#fff', fontSize: '12px', fontWeight: '700' }}>✓</span>
                </div>
                <div
                  style={{
                    width: '2px',
                    height: '24px',
                    backgroundColor: '#D6DAE0',
                    margin: '4px 0',
                  }}
                />
              </div>
              <div>
                <p style={{ fontSize: '14px', fontWeight: '600', margin: '2px 0 0' }}>
                  {t.common.uploadRequiredDocs}
                </p>
                <p style={{ fontSize: '12px', color: '#667085', margin: '4px 0 0' }}>
                  {t.common.uploadRequiredDocsDesc}
                </p>
              </div>
            </div>
            <div style={{ display: 'flex', gap: '12px' }}>
              <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
                <div
                  style={{
                    width: '24px',
                    height: '24px',
                    borderRadius: '50%',
                    border: '2px solid #D6DAE0',
                    backgroundColor: '#fff',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                  }}
                ></div>
              </div>
              <div>
                <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                  <p style={{ fontSize: '14px', fontWeight: '400', margin: '2px 0 0' }}>
                    {t.common.downloadApp}
                  </p>
                  <span
                    style={{
                      fontSize: '11px',
                      fontWeight: '600',
                      color: '#F59E0B',
                      backgroundColor: '#FEF3C7',
                      padding: '2px 8px',
                      borderRadius: '10px',
                    }}
                  >
                    {t.common.notDone}
                  </span>
                </div>
                <p style={{ fontSize: '12px', color: '#667085', margin: '4px 0 8px' }}>
                  {t.common.downloadAppDesc}
                </p>
                <button
                  onClick={() =>
                    window.open('https://haupcar.com/download', '_blank', 'noopener,noreferrer')
                  }
                  style={{
                    padding: '8px 16px',
                    border: '1px solid #0048B3',
                    borderRadius: '8px',
                    backgroundColor: '#fff',
                    cursor: 'pointer',
                    fontSize: '13px',
                    color: '#0048B3',
                  }}
                >
                  {t.common.downloadApp}
                </button>
              </div>
            </div>
          </div>
        </div>
        <div
          style={{
            padding: '16px',
            borderTop: '1px solid #f0f0f0',
            flexShrink: 0,
            opacity: successVisible ? 1 : 0,
            animation: successVisible ? 'fadeSlideUp 0.5s ease 0.6s both' : 'none',
          }}
        >
          <button
            onClick={() => onNext()}
            style={{
              width: '100%',
              padding: '14px',
              backgroundColor: '#0048B3',
              color: '#fff',
              border: 'none',
              borderRadius: '24px',
              cursor: 'pointer',
              fontWeight: '600',
              fontSize: '16px',
            }}
          >
            {t.common.manageYourCars}
          </button>
        </div>
      </div>
    )
  }

  const renderSelectedCarView = () => {
    const carInfo = expandedCars.find((c) => c.id === selectedCarId)
    const carData = data.cars[selectedCarId!] || {}
    return (
      <div style={pageStyle}>
        <TopBar onExit={onExit} />
        <div style={{ flex: 1, overflowY: 'auto', padding: '16px' }}>
          <h2 style={{ fontSize: '18px', fontWeight: '700', color: '#212427', margin: '0 0 4px' }}>
            {t.step5.title}
          </h2>
          <p style={{ fontSize: '14px', color: '#0048B3', fontWeight: '600', margin: '0 0 24px' }}>
            {carInfo?.label}
          </p>

          <div style={{ marginBottom: '16px' }}>
            <label style={labelStyle}>{t.step5.licensePlate}</label>
            <div style={{ display: 'flex', gap: '8px' }}>
              <input
                style={{ ...inputStyle, width: '40%' }}
                placeholder={t.step5.platePrefix}
                value={carData.platePrefix || ''}
                onChange={(e) =>
                  handleCarFieldChange(selectedCarId!, 'platePrefix', e.target.value)
                }
              />
              <input
                style={{ ...inputStyle, flex: 1 }}
                placeholder={t.step5.plateNumber}
                value={carData.plateNumber || ''}
                onChange={(e) =>
                  handleCarFieldChange(selectedCarId!, 'plateNumber', e.target.value)
                }
              />
            </div>
          </div>
          <div style={{ marginBottom: '16px' }}>
            <label style={labelStyle}>{t.step5.province}</label>
            <input
              style={inputStyle}
              value={carData.registrationProvince || ''}
              onChange={(e) =>
                handleCarFieldChange(selectedCarId!, 'registrationProvince', e.target.value)
              }
              placeholder={t.step5.provincePlaceholder}
            />
          </div>
          <div style={{ marginBottom: '16px' }}>
            <label style={labelStyle}>{t.step5.vin}</label>
            <input
              style={inputStyle}
              placeholder={t.step5.vinPlaceholder}
              value={carData.vin || ''}
              onChange={(e) => handleCarFieldChange(selectedCarId!, 'vin', e.target.value)}
            />
          </div>
          <div style={{ marginBottom: '16px' }}>
            <label style={labelStyle}>{t.step5.taxExpiry}</label>
            <input
              type="date"
              style={inputStyle}
              value={carData.taxExpirationDate || ''}
              onChange={(e) =>
                handleCarFieldChange(selectedCarId!, 'taxExpirationDate', e.target.value)
              }
            />
          </div>

          <div style={{ marginBottom: '16px' }}>
            <label style={labelStyle}>{t.step5.isCompanyRegistered}</label>
            <div style={{ display: 'flex', gap: '16px', marginTop: '8px' }}>
              <label
                style={{ display: 'flex', alignItems: 'center', gap: '8px', cursor: 'pointer' }}
              >
                <input
                  type="radio"
                  name="company"
                  checked={carData.isCompanyRegistered === true}
                  onChange={() => handleCarFieldChange(selectedCarId!, 'isCompanyRegistered', true)}
                />{' '}
                {t.common.yes}
              </label>
              <label
                style={{ display: 'flex', alignItems: 'center', gap: '8px', cursor: 'pointer' }}
              >
                <input
                  type="radio"
                  name="company"
                  checked={carData.isCompanyRegistered === false}
                  onChange={() =>
                    handleCarFieldChange(selectedCarId!, 'isCompanyRegistered', false)
                  }
                />{' '}
                {t.common.no}
              </label>
            </div>
          </div>
          <div style={{ marginBottom: '24px' }}>
            <label style={labelStyle}>{t.step5.isDirector}</label>
            <div style={{ display: 'flex', gap: '16px', marginTop: '8px' }}>
              <label
                style={{ display: 'flex', alignItems: 'center', gap: '8px', cursor: 'pointer' }}
              >
                <input
                  type="radio"
                  name="director"
                  checked={carData.isDirector === true}
                  onChange={() => handleCarFieldChange(selectedCarId!, 'isDirector', true)}
                />{' '}
                {t.common.yes}
              </label>
              <label
                style={{ display: 'flex', alignItems: 'center', gap: '8px', cursor: 'pointer' }}
              >
                <input
                  type="radio"
                  name="director"
                  checked={carData.isDirector === false}
                  onChange={() => handleCarFieldChange(selectedCarId!, 'isDirector', false)}
                />{' '}
                {t.common.no}
              </label>
            </div>
          </div>

          <div style={{ marginBottom: '24px' }}>
            <label style={labelStyle}>{t.step5.companyCertificate}</label>
            {renderUploadBox(
              `car-${selectedCarId}:companyCertificateUrl`,
              carData.companyCertificateUrl,
            )}
          </div>
          <div style={{ marginBottom: '24px' }}>
            <label style={labelStyle}>{t.step5.registrationBook}</label>
            {renderUploadBox(
              `car-${selectedCarId}:registrationBookUrl`,
              carData.registrationBookUrl,
            )}
          </div>
          <div style={{ marginBottom: '24px' }}>
            <label style={labelStyle}>{t.step5.taxLabel}</label>
            {renderUploadBox(`car-${selectedCarId}:taxLabelUrl`, carData.taxLabelUrl)}
          </div>
        </div>

        <div style={bottomNavStyle}>
          <button onClick={() => setSelectedCarId(null)} style={skipBtnStyle}>
            {t.step5.skipAndSave}
          </button>
          <div style={{ display: 'flex', gap: '12px' }}>
            <button onClick={() => setSelectedCarId(null)} style={backBtnStyle}>
              {t.common.back}
            </button>
            <button onClick={() => setSelectedCarId(null)} style={saveBtnStyle}>
              {t.common.save}
            </button>
          </div>
        </div>
      </div>
    )
  }

  const renderMainView = () => {
    return (
      <div style={pageStyle}>
        <TopBar onExit={onExit} />
        <div style={{ padding: '16px 16px 0' }}>
          <p
            style={{
              fontSize: '12px',
              color: '#667085',
              fontWeight: '600',
              margin: '0 0 4px',
              textTransform: 'uppercase',
            }}
          >
            {t.common.stepOf(5, 5)}
          </p>
          <h2 style={{ fontSize: '20px', fontWeight: '700', color: '#212427', margin: '0 0 16px' }}>
            {t.step5.title}
          </h2>
          <div style={{ display: 'flex', gap: '4px', marginBottom: '24px' }}>
            <div
              style={{ flex: 1, height: '4px', backgroundColor: '#00CCCC', borderRadius: '2px' }}
            />
            <div
              style={{ flex: 1, height: '4px', backgroundColor: '#00CCCC', borderRadius: '2px' }}
            />
            <div
              style={{ flex: 1, height: '4px', backgroundColor: '#00CCCC', borderRadius: '2px' }}
            />
            <div
              style={{ flex: 1, height: '4px', backgroundColor: '#00CCCC', borderRadius: '2px' }}
            />
            <div
              style={{ flex: 1, height: '4px', backgroundColor: '#00CCCC', borderRadius: '2px' }}
            />
          </div>
        </div>

        <div style={{ flex: 1, overflowY: 'auto', padding: '0 16px 16px' }}>
          <div style={{ marginBottom: '24px' }}>
            <label style={labelStyle}>{t.step5.idCard}</label>
            {renderUploadBox('idCardUrl', data.idCardUrl)}
          </div>

          <div style={{ marginBottom: '24px' }}>
            <label style={labelStyle}>{t.step5.bankBook}</label>
            {renderUploadBox('bankBookUrl', data.bankBookUrl)}
          </div>

          <div style={{ marginBottom: '24px' }}>
            <label style={labelStyle}>{t.step5.bankHolderSame}</label>
            <div style={{ display: 'flex', gap: '16px', marginTop: '8px' }}>
              <label
                style={{ display: 'flex', alignItems: 'center', gap: '8px', cursor: 'pointer' }}
              >
                <input
                  type="radio"
                  name="bankOwner"
                  checked={data.isBankHolderSameAsOwner === true}
                  onChange={() => onDataChange({ ...data, isBankHolderSameAsOwner: true })}
                />{' '}
                {t.common.yes}
              </label>
              <label
                style={{ display: 'flex', alignItems: 'center', gap: '8px', cursor: 'pointer' }}
              >
                <input
                  type="radio"
                  name="bankOwner"
                  checked={data.isBankHolderSameAsOwner === false}
                  onChange={() => onDataChange({ ...data, isBankHolderSameAsOwner: false })}
                />{' '}
                {t.common.no}
              </label>
            </div>
          </div>

          <div style={{ marginBottom: '32px' }}>
            <label style={labelStyle}>{t.step5.consentLetter}</label>
            {renderUploadBox('consentLetterUrl', data.consentLetterUrl)}
          </div>

          <h3 style={{ fontSize: '16px', fontWeight: '700', color: '#212427', margin: '0 0 12px' }}>
            {t.step5.perCarDocs}
          </h3>
          <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
            {expandedCars.map((car) => {
              const hasData = !!data.cars[car.id]
              return (
                <div
                  key={car.id}
                  onClick={() => setSelectedCarId(car.id)}
                  style={{
                    border: '1px solid #D6DAE0',
                    borderRadius: '12px',
                    padding: '16px',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'space-between',
                    cursor: 'pointer',
                    backgroundColor: '#fff',
                  }}
                >
                  <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                    <span style={{ fontSize: '14px', fontWeight: '600', color: '#212427' }}>
                      {car.label}
                    </span>
                    {hasData && <span style={{ color: '#00CCCC', fontSize: '14px' }}>✓</span>}
                  </div>
                  <span style={{ color: '#0048B3', fontSize: '18px', fontWeight: 'bold' }}>›</span>
                </div>
              )
            })}
            {expandedCars.length === 0 && (
              <div
                style={{
                  textAlign: 'center',
                  padding: '24px 16px',
                  backgroundColor: '#F9FAFB',
                  borderRadius: '12px',
                  border: '1px dashed #D6DAE0',
                }}
              >
                <p style={{ color: '#667085', fontSize: '14px', margin: '0 0 12px' }}>
                  {t.step5.noCars}
                </p>
                <button
                  onClick={onBack}
                  style={{
                    padding: '8px 16px',
                    border: '1px solid #0048B3',
                    borderRadius: '8px',
                    backgroundColor: '#fff',
                    cursor: 'pointer',
                    fontSize: '13px',
                    color: '#0048B3',
                    fontWeight: '500',
                  }}
                >
                  {t.step5.backToAddCar}
                </button>
              </div>
            )}
          </div>
        </div>

        <div style={{ ...bottomNavStyle, flexDirection: 'column' }}>
          <button onClick={handleSubmit} style={skipBtnStyle}>
            {t.common.skipAndSubmit}
          </button>
          <div style={{ display: 'flex', gap: '12px', width: '100%' }}>
            <button onClick={onBack} style={backBtnStyle}>
              {t.common.back}
            </button>
            <button
              onClick={handleSubmit}
              disabled={isSubmitting}
              style={{ ...saveBtnStyle, backgroundColor: isSubmitting ? '#99afd4' : '#0048B3' }}
            >
              {isSubmitting ? t.common.submitting : t.step5.submit}
            </button>
          </div>
        </div>
      </div>
    )
  }

  return (
    <>
      {showSuccessLoading
        ? renderSuccessLoadingView()
        : showSuccess
        ? renderSuccessView()
        : selectedCarId
        ? renderSelectedCarView()
        : renderMainView()}
      <input
        type="file"
        ref={fileInputRef}
        style={{ display: 'none' }}
        onChange={handleFileChange}
        accept="image/*"
      />
    </>
  )
}

const pageStyle: React.CSSProperties = {
  fontFamily: 'Poppins, sans-serif',
  backgroundColor: '#fff',
  minHeight: '100vh',
  maxWidth: '500px',
  margin: '0 auto',
  display: 'flex',
  flexDirection: 'column',
  boxSizing: 'border-box',
}

const labelStyle: React.CSSProperties = {
  fontSize: '14px',
  fontWeight: '600',
  color: '#212427',
  display: 'block',
  marginBottom: '8px',
}

const inputStyle: React.CSSProperties = {
  width: '100%',
  padding: '12px 14px',
  border: '1px solid #D6DAE0',
  borderRadius: '8px',
  fontSize: '14px',
  fontFamily: 'Poppins, sans-serif',
  boxSizing: 'border-box',
  outline: 'none',
  backgroundColor: '#fff',
  color: '#212427',
}

const bottomNavStyle: React.CSSProperties = {
  padding: '16px',
  borderTop: '1px solid #f0f0f0',
  flexShrink: 0,
  display: 'flex',
  flexDirection: 'column',
  gap: '12px',
}

const skipBtnStyle: React.CSSProperties = {
  background: 'none',
  border: 'none',
  color: '#0048B3',
  fontSize: '14px',
  fontWeight: '600',
  cursor: 'pointer',
  textDecoration: 'underline',
  fontFamily: 'Poppins, sans-serif',
  alignSelf: 'center',
}

const backBtnStyle: React.CSSProperties = {
  flex: 1,
  padding: '12px',
  backgroundColor: '#fff',
  color: '#0048B3',
  border: '1.5px solid #0048B3',
  borderRadius: '24px',
  cursor: 'pointer',
  fontWeight: '500',
  fontSize: '15px',
  fontFamily: 'Poppins, sans-serif',
}

const saveBtnStyle: React.CSSProperties = {
  flex: 1,
  padding: '12px',
  backgroundColor: '#0048B3',
  color: '#fff',
  border: 'none',
  borderRadius: '24px',
  cursor: 'pointer',
  fontWeight: '500',
  fontSize: '15px',
  fontFamily: 'Poppins, sans-serif',
}

export default CarWithDriverStep5
